import {AppUrls} from 'shared/config/model/urls';
import {Pages} from 'shared/config/model/pages';

import React from 'react';
import {useSelector} from 'react-redux';
import {i18n} from '@lingui/core';
import {t} from '@lingui/core/macro';

import useOpenState from 'shared/lib/hooks/useOpenState';
import useListParams from 'shared/lib/hooks/useListParams';
import useResetPageOnEmpty from 'shared/lib/hooks/useResetPageOnEmpty';
import ListPage from 'shared/ui/ListPage/ListPage';
import PlugNoAccess from 'shared/ui/Plugs/PlugNoAccess/PlugNoAccess';
import {IColumn} from 'shared/ui/Table/Table';
import ListToolbar from 'shared/ui/ListToolbar/ListToolbar';
import TableToolbar from 'shared/ui/TableToolbar/TableToolbar';
import ListFiltersModal from 'shared/ui/Modal/ListFiltersModal/ListFiltersModal';
import {userReportAPI} from 'features/userReports/api/userReportAPI';
import {USER_REPORT_FILTER_KEYS} from 'features/userReports/lib/userReportFilters';
import {CreateGatedButton, userSelector, useHasTariffLimit, useIsUnderUserLimit} from 'entities/user';

import UserReportRow from '../UserReportRow/UserReportRow';
import UserReportsWelcome from '../UserReportsWelcome/UserReportsWelcome';
import UserReportModal from '../UserReportModal/UserReportModal';
import UserReportsFiltersForm from '../UserReportsFiltersForm/UserReportsFiltersForm';

const UserReports: React.FC = () => {
    const user = useSelector(userSelector);
    const hasAccess = useHasTariffLimit('reports_max_count_limit');
    const canCreate = useIsUnderUserLimit('reports_max_count_limit', 'reports_count');

    const limit = user?.tariff?.reports_max_count_limit ?? 0;
    const count = user?.reports_count ?? 0;

    const {args, sort, setSort, hasFilters, search, onSearch} = useListParams({
        filterKeys: USER_REPORT_FILTER_KEYS,
        defaultSort: {field: 'id', direction: 'desc'},
    });
    const filtersModal = useOpenState();

    const {data, isLoading, isFetching, refetch} =
        userReportAPI.useGetUserReportsQuery(args, {skip: !hasAccess});
    const items = data?.items ?? [];
    const isRedirecting = useResetPageOnEmpty(data);

    if (hasAccess && !isLoading && !hasFilters && data?.totalItems === 0) {
        return <UserReportsWelcome/>;
    }

    const columns: IColumn[] = [
        {label: t`Название`, sort: 'name'},
        {label: t`Период`, sort: 'period'},
        {label: t`Кол-во мониторов`},
        {label: t`Куда отправляем`},
        {label: t`Баннеры отключены`},
        {label: t`Дата создания`, sort: 'created_at'},
        {label: t`Статус`, sort: 'is_active'},
        {label: ''},
    ];

    return (
        <ListPage
            documentTitle={`${t`Отчёты`} | ${t`Overseer`}`}
            toolbar={
                <ListToolbar
                    title={i18n._(Pages.USER_REPORTS)}
                    count={limit > 0 ? count : undefined}
                    maxCount={limit > 0 ? limit : undefined}
                    backUrl={AppUrls.HOME}
                    createButton={
                        <CreateGatedButton
                            label={t`Создать отчёт`}
                            canCreate={canCreate}
                            exceededType="reports"
                            Modal={UserReportModal}
                        />
                    }
                    actions={
                        <TableToolbar
                            onSearch={onSearch}
                            searchValue={search}
                            onFilter={filtersModal.open}
                            onRefresh={refetch}
                            isRefreshing={isFetching}
                        />
                    }
                />
            }
            plug={!hasAccess && <PlugNoAccess/>}
            columns={columns}
            items={items}
            rowFactory={(item) =>
                <UserReportRow
                    key={item.uuid}
                    item={item}
                    user={user}
                    canCreate={canCreate}
                />
            }
            isLoading={hasAccess && (isLoading || isRedirecting)}
            pagination={data}
            sort={sort}
            setSort={setSort}
            emptyStateComponent={hasFilters ? t`Ничего не найдено` : true}
        >
            <ListFiltersModal
                open={filtersModal.isOpen}
                handleClose={filtersModal.close}
                Form={UserReportsFiltersForm}
            />
        </ListPage>
    );
};

export default UserReports;
